import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { MediaItem } from "@/mocks/media";
import { typeLabels } from "@/mocks/filters";

export default function HeroCarousel({ items }: { items: MediaItem[] }) {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const timer = useRef<number | null>(null);

  useEffect(() => {
    if (paused) return;
    timer.current = window.setInterval(() => {
      setActive((prev) => (prev + 1) % items.length);
    }, 6000);
    return () => {
      if (timer.current) window.clearInterval(timer.current);
    };
  }, [paused, items.length]);

  const current = items[active];

  return (
    <section
      className="relative w-full"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="relative h-[520px] w-full overflow-hidden bg-primary-950 md:h-[600px]">
        {items.map((item, index) => (
          <div
            key={item.id}
            className={`absolute inset-0 transition-opacity duration-[900ms] ${
              index === active ? "opacity-100" : "opacity-0"
            }`}
          >
            <img
              src={item.backdrop}
              alt={`${item.title} 剧照`}
              className={`h-full w-full object-cover object-top ${index === active ? "animate-ken" : ""}`}
            />
          </div>
        ))}
        <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-primary-950/60 to-primary-950/25" />
        <div className="absolute inset-0 bg-gradient-to-r from-primary-950/85 via-primary-950/35 to-transparent" />

        <div className="relative z-10 flex h-full w-full flex-col justify-end px-4 pb-10 md:px-6 md:pb-14">
          <div key={current.id} className="animate-fade-up max-w-2xl">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-sm bg-accent-500 px-2 py-1 font-label text-[10px] font-bold uppercase tracking-[0.12em] text-background-50">
                编辑精选
              </span>
              <span className="rounded-sm border border-background-50/30 px-2 py-1 font-label text-[10px] text-background-50/80">
                {typeLabels[current.type]}
              </span>
              <span className="font-label text-[11px] text-background-50/70">{current.status}</span>
            </div>
            <h1 className="mt-4 font-heading text-3xl font-black leading-tight tracking-tight text-background-50 md:text-5xl">
              {current.title}
            </h1>
            <p className="mt-2 font-label text-[11px] uppercase tracking-[0.24em] text-background-50/55">
              {current.enTitle}
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 font-label text-xs text-background-50/75">
              <span className="flex items-center gap-1 font-semibold text-accent-400">
                <i className="ri-star-fill text-[11px]" />
                {current.rating.toFixed(1)}
              </span>
              <span>{current.year}</span>
              <span className="h-3 w-px bg-background-50/25" />
              <span>{current.genres.join(" / ")}</span>
              <span className="h-3 w-px bg-background-50/25" />
              <span>{current.region}</span>
              <span className="h-3 w-px bg-background-50/25" />
              <span>{current.episodes > 1 ? `全 ${current.episodes} 集` : current.duration}</span>
            </div>
            <p className="mt-4 hidden max-w-xl text-[13px] leading-relaxed text-background-50/70 sm:line-clamp-2">
              {current.synopsis}
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Link
                to={`/detail/${current.id}`}
                className="flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600"
              >
                <i className="ri-play-fill flex h-4 w-4 items-center justify-center" />
                查看详情
              </Link>
              <Link
                to={`/category/${current.type}`}
                className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-50/35 px-5 py-2.5 text-sm text-background-50 transition-colors hover:border-background-50/70 hover:bg-background-50/10"
              >
                更多{typeLabels[current.type]}
                <i className="ri-arrow-right-line flex h-4 w-4 items-center justify-center" />
              </Link>
            </div>
          </div>
        </div>

        <div className="absolute bottom-10 right-4 z-10 hidden items-center gap-2 md:right-6 md:flex">
          {items.map((item, index) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setActive(index)}
              aria-label={`切换到 ${item.title}`}
              className={`cursor-pointer rounded-full transition-all duration-300 ${
                index === active ? "h-1.5 w-8 bg-accent-500" : "h-1.5 w-3 bg-background-50/40 hover:bg-background-50/70"
              }`}
            />
          ))}
        </div>
      </div>

      <div className="hidden border-b border-background-200 bg-background-100/80 lg:block">
        <div className="flex w-full divide-x divide-background-200 px-4 md:px-6">
          {items.map((item, index) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setActive(index)}
              className={`group flex flex-1 cursor-pointer items-center gap-3 px-4 py-3 text-left transition-colors ${
                index === active ? "bg-background-50" : "hover:bg-background-50/60"
              }`}
            >
              <span
                className={`font-label text-[11px] font-bold ${
                  index === active ? "text-accent-600" : "text-foreground-400"
                }`}
              >
                0{index + 1}
              </span>
              <span className="min-w-0">
                <span className="line-clamp-1-custom block text-[13px] font-medium text-foreground-900">
                  {item.title}
                </span>
                <span className="mt-0.5 block font-label text-[10px] text-foreground-500">
                  {typeLabels[item.type]} · {item.year}
                </span>
              </span>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}